import React, { useState, ReactNode, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Footer from './Footer';
import {
  Box,
  CssBaseline,
  Drawer,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Build as BuildIcon,
  Inventory as InventoryIcon,
  BarChart as ReportsIcon,
  Settings as SettingsIcon,
  Warehouse as WarehouseIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
  ReceiptLong as ReceiptLongIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  Backup as BackupIcon,
  Person as PersonIcon,
  Storefront as StorefrontIcon,
  Dashboard as DashboardIcon,
  ShoppingBag as ShoppingBagIcon,
  Tune as TuneIcon,
  Description as DescriptionIcon,
  LocalOffer as LocalOfferIcon,
  AccountBalance as BankIcon,
  DataUsage as DataUsageIcon,
  Speed as SpeedIcon,
  AttachMoney as AttachMoneyIcon,
  Engineering as EngineeringIcon,
  RecordVoiceOver as SuggestionIcon
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import { logoutUser } from '../services/authService';
import { hasPermission } from '../services/permissionService';
import { User } from '../utils/types';
import { getSystemSettings, SystemSettings, DEFAULT_SETTINGS } from '../services/systemSettingsService';
import EnhancedSidebar from './EnhancedSidebar';
import ModernAppBar from './ModernAppBar';
import { restoreAdminFavicon } from '../utils/faviconUtils';
import { useTeamNotifications } from '../hooks/useTeamNotifications';
import TeamChatNotificationBadge from './TeamChatNotificationBadge';

// Define los tipos de permisos para el navegador
type NavPermission = keyof User['permissions'];

interface LayoutProps {
  children?: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { currentUser, setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { unreadCount } = useTeamNotifications();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [financialOpen, setFinancialOpen] = useState(false);

  const [systemSettings, setSystemSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };



  const handleLogout = async () => {
    try {
      await logoutUser();
      setCurrentUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // تحميل إعدادات النظام واستعادة الأيقونة الافتراضية
  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const settings = await getSystemSettings();
        setSystemSettings(settings);
      } catch (error) {
        console.error('Error loading system settings:', error);
      }
    };

    loadSystemSettings();

    // استعادة الأيقونة الافتراضية للوحة التحكم
    restoreAdminFavicon();
  }, []);

  // طباعة صلاحيات المستخدم للتصحيح
  useEffect(() => {
    if (currentUser) {
      console.log('User permissions:', currentUser.permissions);
      console.log('Can view expenses:', currentUser.permissions.canViewExpenses);

      // إضافة صلاحية عرض المصروفات إذا لم تكن موجودة
      if (currentUser.permissions.canViewExpenses === undefined) {
        console.log('Adding canViewExpenses permission');
        setCurrentUser({
          ...currentUser,
          permissions: {
            ...currentUser.permissions,
            canViewExpenses: true
          }
        });
      }
    }
  }, [currentUser, setCurrentUser]);

  // العناصر الرئيسية في القائمة - استخدام useMemo لتحسين الأداء
  const mainNavItems = React.useMemo(() => [
    {
      name: 'لوحة التحكم',
      icon: <DashboardIcon />,
      path: '/admin',
      permission: 'canViewDashboard' as NavPermission
    },
    {
      name: 'الصيانات',
      icon: <BuildIcon />,
      path: '/admin/maintenance-records',
      permission: 'canAccessMaintenance' as NavPermission
    },
    {
      name: 'نقطة البيع',
      icon: <ShoppingCartIcon />,
      path: '/admin/pos',
      permission: 'canViewSales' as NavPermission
    },
    {
      name: 'المتجر الإلكتروني',
      icon: <StorefrontIcon />,
      path: '/',
      permission: 'canViewSales' as NavPermission
    },
    {
      name: 'إدارة الطلبات',
      icon: <ShoppingBagIcon />,
      path: '/admin/orders',
      permission: 'canViewSales' as NavPermission
    },
    {
      name: 'اقتراحات الأسعار',
      icon: <SuggestionIcon />,
      path: '/admin/product-suggestions',
      permission: 'canViewSales' as NavPermission
    },
    {
      name: 'إدارة العروض',
      icon: <LocalOfferIcon />,
      path: '/admin/promotions',
      permission: 'canAccessSettings' as NavPermission
    },

    {
      name: 'بنك الصيانة',
      icon: <BankIcon />,
      path: '/admin/maintenance-bank',
      permission: 'canViewMaintenance' as NavPermission
    },

    {
      name: 'التواصل الجماعي',
      icon: <TeamChatNotificationBadge />,
      path: '/admin/team',
      permission: 'canViewDashboard' as NavPermission,
      chip: unreadCount > 0 ? `${unreadCount}` : 'جديد',
      badge: unreadCount > 0
    }
  ], [unreadCount]);

  // عناصر قائمة إدارة المنتجات
  const inventoryNavItems = React.useMemo(() => [
    {
      name: 'المنتجات',
      icon: <InventoryIcon />,
      path: '/admin/inventory',
      permission: 'canAccessInventory' as NavPermission
    },
    {
      name: 'الفئات',
      icon: <CategoryIcon />,
      path: '/admin/category',
      permission: 'canAccessInventory' as NavPermission
    },
    {
      name: 'المخازن',
      icon: <WarehouseIcon />,
      path: '/admin/warehouse',
      permission: 'canAccessInventory' as NavPermission
    },
    {
      name: 'الفروع',
      icon: <BusinessIcon />,
      path: '/admin/branches',
      permission: 'canAccessSettings' as NavPermission
    }
  ], []);

  // عناصر قائمة الإعدادات - استخدام useMemo لتحسين الأداء
  const settingsNavItems = React.useMemo(() => [
    {
      name: 'الإعدادات العامة',
      icon: <SettingsIcon />,
      path: '/admin/settings',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'إعدادات متقدمة',
      icon: <TuneIcon />,
      path: '/admin/advanced-settings',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'المتجر الإلكتروني',
      icon: <StorefrontIcon />,
      path: '/admin/store-settings',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'إدارة الصفحات',
      icon: <DescriptionIcon />,
      path: '/admin/pages',
      permission: 'canAccessSettings' as NavPermission
    },

    {
      name: 'رسائل واتساب',
      icon: <WhatsAppIcon />,
      path: '/admin/whatsapp',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'WhatsApp الذكي',
      icon: <WhatsAppIcon />,
      path: '/admin/whatsapp-baileys',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'أدوات الإدارة',
      icon: <SettingsIcon />,
      path: '/admin/admin-tools',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'النسخ الاحتياطي',
      icon: <BackupIcon />,
      path: '/admin/backup',
      permission: 'canManageBackup' as NavPermission
    },
    {
      name: 'استخدام فايربيز',
      icon: <DataUsageIcon />,
      path: '/admin/firebase-usage',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'مراقبة الأداء',
      icon: <SpeedIcon />,
      path: '/admin/performance',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'اختبار العملة',
      icon: <AttachMoneyIcon />,
      path: '/admin/currency-test',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'الإعدادات المتقدمة',
      icon: <SettingsIcon />,
      path: '/admin/advanced-settings',
      permission: 'canAccessSettings' as NavPermission
    },
  ], []);

  // عناصر قائمة الشؤون المالية
  const financialNavItems = React.useMemo(() => [
    {
      name: 'العملاء',
      icon: <PersonIcon />,
      path: '/admin/customers',
      permission: 'canViewCustomers' as NavPermission
    },
    {
      name: 'الفنيين',
      icon: <EngineeringIcon />,
      path: '/admin/technicians',
      permission: 'canAccessSettings' as NavPermission
    },
    {
      name: 'المبيعات',
      icon: <ReceiptIcon />,
      path: '/admin/sales',
      permission: 'canViewSales' as NavPermission
    },
    {
      name: 'الفواتير والمرتجعات',
      icon: <ReceiptLongIcon />,
      path: '/admin/invoices',
      permission: 'canViewSales' as NavPermission
    },
    {
      name: 'طلبات المتجر',
      icon: <ShoppingBagIcon />,
      path: '/admin/orders',
      permission: 'canViewSales' as NavPermission
    },
    {
      name: 'الديون',
      icon: <ReceiptLongIcon />,
      path: '/admin/debts',
      permission: 'canAccessDebts' as NavPermission
    },
    {
      name: 'المصروفات',
      icon: <ReceiptLongIcon />,
      path: '/admin/expenses',
      permission: 'canViewExpenses' as NavPermission
    },
    {
      name: 'التقارير',
      icon: <ReportsIcon />,
      path: '/admin/reports',
      permission: 'canAccessReports' as NavPermission
    },
    {
      name: 'تقارير الفنيين',
      icon: <EngineeringIcon />,
      path: '/admin/technician-reports',
      permission: 'canAccessReports' as NavPermission
    },
    {
      name: 'ربط الأجهزة بالفنيين',
      icon: <EngineeringIcon />,
      path: '/admin/technician-device-report',
      permission: 'canAccessReports' as NavPermission
    },
    {
      name: 'التقرير الشهري الشامل',
      icon: <ReportsIcon />,
      path: '/admin/monthly-report',
      permission: 'canAccessReports' as NavPermission
    }
  ], []);

  // فتح القوائم الفرعية تلقائيًا بناءً على المسار الحالي
  useEffect(() => {
    // التحقق من مسارات الإعدادات
    const isSettingsPath = settingsNavItems.some(item => item.path === location.pathname);
    if (isSettingsPath && !settingsOpen && !drawerCollapsed) {
      setSettingsOpen(true);
    }

    // التحقق من مسارات إدارة المنتجات
    const isInventoryPath = inventoryNavItems.some(item => item.path === location.pathname);
    if (isInventoryPath && !inventoryOpen && !drawerCollapsed) {
      setInventoryOpen(true);
    }

    // التحقق من مسارات الشؤون المالية
    const isFinancialPath = financialNavItems.some(item => item.path === location.pathname);
    if (isFinancialPath && !financialOpen && !drawerCollapsed) {
      setFinancialOpen(true);
    }
  }, [location.pathname, settingsOpen, inventoryOpen, financialOpen, drawerCollapsed, settingsNavItems, inventoryNavItems, financialNavItems]);

  const drawer = (
    <EnhancedSidebar
      collapsed={drawerCollapsed}
      onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
      navItems={mainNavItems}
      inventoryNavItems={inventoryNavItems}
      financialNavItems={financialNavItems}
      settingsNavItems={settingsNavItems}
      currentUser={currentUser}
      hasPermission={(user: any, permission: string) => hasPermission(user, permission as NavPermission)}
      onItemClick={() => {
        if (isMobile) {
          setMobileOpen(false);
        }
      }}
    />
  );





  const currentPageTitle =
    mainNavItems.find(item => item.path === location.pathname)?.name ||
    inventoryNavItems.find(item => item.path === location.pathname)?.name ||
    financialNavItems.find(item => item.path === location.pathname)?.name ||
    settingsNavItems.find(item => item.path === location.pathname)?.name ||
    'لوحة التحكم';

  return (
    <Box sx={{ display: 'flex', direction: 'rtl' }}>
      <CssBaseline />
      <ModernAppBar
        currentUser={currentUser}
        systemSettings={systemSettings}
        onMenuToggle={handleDrawerToggle}
        onLogout={handleLogout}
        isMobile={isMobile}
        currentPageTitle={currentPageTitle}
      />

      <Box
        component="nav"
        sx={{
          width: {
            sm: isMobile ? 0 : (drawerCollapsed ? 65 : 280)
          },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          anchor="right"
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            width: isMobile ? 280 : (drawerCollapsed ? 65 : 280),
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            overflowX: 'hidden',
            '& .MuiDrawer-paper': {
              width: isMobile ? 280 : (drawerCollapsed ? 65 : 280),
              boxSizing: 'border-box',
              overflowX: 'hidden',
              paddingTop: '64px',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              border: 'none',
              boxShadow: '0 0 20px rgba(0,0,0,0.1)'
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: {
            xs: '100%',
            sm: `calc(100% - ${isMobile ? 0 : (drawerCollapsed ? 65 : 280)}px)`
          },
          mt: '64px',
          direction: 'rtl',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 64px)',
          pb: 2,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {children || <Outlet />}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;
